import React, { memo, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FaStar,
  FaRegStar,
  FaHeart,
  FaShoppingCart,
  FaUserCircle,
} from "react-icons/fa";
import "../styles/product-card.css";

const IMAGE_BASE_URL = "https://auth.afrogebeya.com/uploads/products";
const PLACEHOLDER_IMAGE = "https://via.placeholder.com/300x300?text=No+Image";

const resolveProductImage = (product) => {
  const storedName = product?.mainImageUrl || product?.mainImage?.storedName;
  return storedName
    ? `${IMAGE_BASE_URL}/${storedName.replace(/\\/g, "/")}`
    : PLACEHOLDER_IMAGE;
};

const createSlug = (name, id) =>
  `${name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}-${id}`;

const formatPrice = (value, currency) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);

const ProductCard = ({ product }) => {
  const {
    id,
    productName,
    sellerPhone,
    currency = "ETB",
    price = 0,
    discountPrice,
    averageRating = 0,
    totalRatings = 0,
    availableQuantity = 0,
    fastShipping,
    isBestSeller,
    isNewArrival,
  } = product;

  const imageUrl = resolveProductImage(product);
  const hasDiscount =
    typeof discountPrice === "number" &&
    discountPrice > 0 &&
    discountPrice < price;

  const rating = Number(averageRating).toFixed(1);
  const roundedRating = Math.round(averageRating);
  const isOutOfStock = availableQuantity === 0;
  const contactPerson = "tel:" + sellerPhone;

  return (
    <article
      className={`product-card shadow-sm rounded-3 ${
        isOutOfStock ? "product-card--disabled" : ""
      }`}
      aria-label={productName}
    >
      {/* Image */}
      <Link
        to={`/products/${createSlug(product.productName, product.id)}`}
        className="product-image-wrapper"
      >
        <img
          src={imageUrl}
          alt={productName}
          loading="lazy"
          className="product-image"
        />

        {/* Badges */}
        {isOutOfStock && <span className="badge bg-danger">Out of stock</span>}
        {fastShipping && !isOutOfStock && (
          <span className="badge bg-success">Fast delivery</span>
        )}
        {isBestSeller && <span className="badge bg-warning">Best Seller</span>}
        {isNewArrival && <span className="badge bg-info">New Arrival</span>}
      </Link>

      {/* Content */}
      <div className="product-content p-2">
        {/* Title */}
        <h6 className="product-title text-truncate" title={productName}>
          <Link to={`/products/${createSlug(product.productName, product.id)}`}>
            {productName}
          </Link>
        </h6>

        {/* Rating */}
        <div className="product-rating d-flex align-items-center">
          {[...Array(5)].map((_, i) =>
            i < roundedRating ? (
              <FaStar key={i} className="text-warning" />
            ) : (
              <FaRegStar key={i} className="text-muted" />
            ),
          )}
          <span className="ms-2 small text-muted">({totalRatings})</span>
        </div>

        {/* Price */}
        <div className="product-price mt-1">
          {hasDiscount ? (
            <>
              <span className="price-discount fw-bold text-danger">
                {formatPrice(discountPrice, currency)}
              </span>
              <span className="price-original text-muted ms-2">
                {formatPrice(price, currency)}
              </span>
              <span className="badge bg-danger ms-2">
                -{Math.round(((price - discountPrice) / price) * 100)}%
              </span>
            </>
          ) : (
            <span className="price-normal fw-bold">
              {formatPrice(price, currency)}
            </span>
          )}
        </div>

        {/* CTA */}
        <div className="d-flex gap-2 mt-2">
          {/* <button
            className="btn btn-sm btn-outline-primary flex-fill"
            disabled={isOutOfStock}
          >
            <FaShoppingCart className="me-1" /> Add to Cart
          </button> */}
          <FaUserCircle size={22} color="#6f42c1" />
          <a href={contactPerson} className=" text-decoration-none">
            {sellerPhone}
          </a>

          <button className="btn btn-sm btn-outline-danger">
            <FaHeart />
          </button>
        </div>
      </div>
    </article>
  );
};

export default memo(ProductCard);
